import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QuoteViewer } from "@/features/quotes/QuoteViewer";
import type { Quote } from "@onsemble/shared";
import { QuoteRejectionDialog } from "@/components/quote-rejection-dialog";
import { useServices } from "@/hooks/use-services";
import { useCustomParts } from "@/hooks/use-custom-parts";
import { useProductsLookup } from "@/hooks/use-products";

// Extend Quote type for the public API response format
interface QuoteApprovalData extends Quote {
  customerFirstName: string;
  customerLastName: string;
  customerType: "person" | "business";
  businessName: string | null;
  organization: {
    name?: string;
    imageUrl?: string;
    url?: string;
    address?: {
      streetAddress?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    } | null;
    phone?: string | null;
  };
}

type AppStatus =
  | "initializing"
  | "loading-quote"
  | "ready"
  | "updating"
  | "error";

export default function QuoteApproval() {
  const { t } = useTranslation();
  const { quote_id } = useParams();
  const code = new URLSearchParams(window.location.search).get("code");
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const [status, setStatus] = useState<AppStatus>("initializing");
  const [data, setData] = useState<QuoteApprovalData | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);

  // Extract product IDs from quote data
  const productIds = useMemo(() => {
    if (!data?.products || !Array.isArray(data.products)) return [];
    return data.products.map((p: any) => p.productId).filter(Boolean);
  }, [data]);

  // Fetch all required data for QuoteViewer
  const {
    products,
    isLoading: productsLoading,
    error: productsError,
  } = useProductsLookup(productIds, productIds.length > 0);
  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useServices();
  const {
    customParts,
    isLoading: customPartsLoading,
    error: customPartsError,
  } = useCustomParts();

  const fetchQuoteData = async () => {
    if (!quote_id || !code) {
      setStatus("error");
      return;
    }

    setStatus("loading-quote");
    try {
      const endpoint = `/api/public/quote-approval/${quote_id}?code=${encodeURIComponent(code)}`;

      const response = await fetch(endpoint, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch quote data");
      }

      const responseData = await response.json();
      setData(responseData);
      setStatus("ready");
    } catch (error) {
      setStatus("error");
    }
  };

  useEffect(() => {
    // Wait for router to initialize - params might be undefined on first render
    if (quote_id === undefined || code === undefined) {
      return;
    }

    // Check if params are empty strings or actually missing
    if (!quote_id || !code) {
      setCodeValid(false);
      setStatus("error");
      return;
    }

    const validateCode = async () => {
      try {
        const endpoint = `/api/public/validate-code/${quote_id}?code=${encodeURIComponent(code)}`;

        const response = await fetch(endpoint, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Request failed");
        }

        const result = await response.json();
        setCodeValid(result.valid);

        // If code is valid, fetch quote data
        if (result.valid) {
          await fetchQuoteData();
        } else {
          setStatus("error");
        }
      } catch (error) {
        setCodeValid(false);
        setStatus("error");
      }
    };

    validateCode();
  }, [quote_id, code]);

  const handleQuoteResponse = async (
    status: "accepted" | "rejected",
    additionalNotes?: string,
  ) => {
    // Input validation
    if (!quote_id || !code) {
      setStatus("error");
      return;
    }

    if (!data) {
      setStatus("error");
      return;
    }

    if (submitted) {
      return;
    }

    if (data.quoteStatus !== "pending_customer_approval") {
      setStatus("error");
      return;
    }

    setStatus("updating");
    try {
      const url = `/api/public/quote-approval/${quote_id}`;

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
          code,
          additional_notes: additionalNotes,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update quote status: ${response.status} ${errorText}`,
        );
      }

      await response.json();

      // Update local state
      if (data) {
        setData({ ...data, quoteStatus: status });
      }
      setSubmitted(true);
      setStatus("ready");
    } catch (error) {
      setStatus("error");
    }
  };

  const handleRejectClick = () => {
    setShowRejectionDialog(true);
  };

  const handleRejectConfirm = async (notes: string) => {
    setShowRejectionDialog(false);
    await handleQuoteResponse("rejected", notes);
  };

  if (status === "initializing" || status === "loading-quote") {
    const loadingMessage =
      status === "initializing"
        ? "Validating code..."
        : "Loading quote data...";
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (!codeValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-red-600">{t("pages.quoteApproval.invalidCode")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              {t("pages.quoteApproval.theQuoteApprovalLink")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If token is valid but no quote data, show error
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-red-600">{t("pages.quoteApproval.quoteNotFound")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              {t("pages.quoteApproval.unableToLoadQuote")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background/95">
      {/* Content */}
      <main className="p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <QuoteViewer
            data={data}
            viewMode="public"
            customer={{
              firstName: data.customerFirstName,
              lastName: data.customerLastName,
              customerType: data.customerType,
              businessName: data.businessName,
            }}
            products={products}
            services={services || []}
            customParts={customParts || []}
            productsLoading={productsLoading}
            servicesLoading={servicesLoading}
            customPartsLoading={customPartsLoading}
            productsError={productsError}
            servicesError={servicesError as Error | null}
            customPartsError={customPartsError as Error | null}
            hidePartDetails={true}
            actions={
              <div className="mt-8 pt-6 border-t">
                {data.quoteStatus === "pending_customer_approval" &&
                !submitted ? (
                  // Show approval/rejection interface only for pending quotes that haven't been submitted
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold mb-2">
                        {t("pages.quoteApproval.yourResponse")}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t("pages.quoteApproval.pleaseReviewTheQuote")}
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <Button
                        size="lg"
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={status === "updating"}
                        onClick={() => handleQuoteResponse("accepted")}
                      >
                        {status === "updating"
                          ? t("pages.quoteApproval.processing")
                          : t("pages.quoteApproval.approveQuote")}
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                        disabled={status === "updating"}
                        onClick={handleRejectClick}
                      >
                        {status === "updating"
                          ? t("pages.quoteApproval.processing")
                          : t("pages.quoteApproval.rejectQuote")}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      {t("pages.quoteApproval.yourDecisionWillBe")}
                    </p>
                  </div>
                ) : (
                  // Show status message for submitted or non-pending quotes
                  <div className="text-center py-8">
                    <div className="mb-4">
                      {data.quoteStatus === "accepted" ? (
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                          <svg
                            className="w-8 h-8 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      ) : data.quoteStatus === "rejected" ? (
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                          <svg
                            className="w-8 h-8 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                          <svg
                            className="w-8 h-8 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {data.quoteStatus === "accepted"
                        ? t("pages.quoteApproval.quoteApproved")
                        : data.quoteStatus === "rejected"
                          ? t("pages.quoteApproval.quoteRejected")
                          : data.quoteStatus === "scheduled_as_job"
                            ? t("pages.quoteApproval.quoteScheduledAsJob")
                            : t("pages.quoteApproval.quoteStatusUpdated")}
                    </h3>
                    <p className="text-gray-600">
                      {data.quoteStatus === "accepted"
                        ? t("pages.quoteApproval.thankYouForApproving")
                        : data.quoteStatus === "rejected"
                          ? t("pages.quoteApproval.yourRejectionHasBeen")
                          : data.quoteStatus === "scheduled_as_job"
                            ? t("pages.quoteApproval.thisQuoteHasBeen")
                            : t("pages.quoteApproval.thisQuoteIsNo")}
                    </p>
                  </div>
                )}
              </div>
            }
          />

          <QuoteRejectionDialog
            open={showRejectionDialog}
            onOpenChange={setShowRejectionDialog}
            onConfirm={handleRejectConfirm}
            isLoading={status === "updating"}
          />
        </div>
      </main>
    </div>
  );
}
